import React, { useState, useEffect, useRef } from "react";
import { DATE_FILTERS } from "@utils/getDateRangeByFilter";
import { TradingCustomRangeCalendar } from "./TradingCustomRangeCalendar";
import "./index.sass";

export const TradingCalendarCard = ({
  selectedDateFilter = DATE_FILTERS.TODAY,
  dateRange,
  dateFilterLabels,
  customRange,
  onDateFilterChange,
  onCustomRangeApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCustomCalendarOpen, setIsCustomCalendarOpen] = useState(false);

  const dateControlRef = useRef(null);

  const handleFilterSelect = (filter) => {
    if (filter === DATE_FILTERS.CUSTOM_RANGE) {
      setIsCustomCalendarOpen(true);
      return;
    }

    setIsCustomCalendarOpen(false);
    onDateFilterChange?.(filter);
    setIsOpen(false);
  };

  const isFilterSelected = (filter) => {
    if (isCustomCalendarOpen) {
      return filter === DATE_FILTERS.CUSTOM_RANGE;
    }

    return selectedDateFilter === filter;
  };

  const handleCustomRangeApply = (range) => {
    onCustomRangeApply?.(range);
    setIsCustomCalendarOpen(false);
    setIsOpen(false);
  };

  const filterOptions = Object.values(DATE_FILTERS).map((filter) => (
    <button
      key={filter}
      className={`trading-calendar-card__option ${
        isFilterSelected(filter) ? "trading-calendar-card__option--active" : ""
      }`}
      onClick={() => handleFilterSelect(filter)}
    >
      <span>{dateFilterLabels[filter]}</span>

      {isFilterSelected(filter) && (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.3497 3.31249C13.6197 3.50499 13.6797 3.87999 13.4872 4.14999L6.88719 13.35C6.78469 13.4925 6.62469 13.585 6.44969 13.5975C6.27469 13.61 6.09969 13.55 5.97469 13.425L2.57469 10.025C2.33969 9.78999 2.33969 9.40999 2.57469 9.17749C2.80969 8.94499 3.18969 8.94249 3.42219 9.17749L6.32469 12.075L12.5122 3.44999C12.7047 3.17999 13.0797 3.11999 13.3497 3.31249Z"
            fill="white"
          />
        </svg>
      )}
    </button>
  ));

  useEffect(() => {
    if (selectedDateFilter !== DATE_FILTERS.CUSTOM_RANGE) {
      setIsCustomCalendarOpen(false);
    }
  }, [selectedDateFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dateControlRef.current &&
        !dateControlRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setIsCustomCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="trading-calendar-card">
      <div className="trading-calendar-card__date-control" ref={dateControlRef}>
        <button
          className="trading-calendar-card__select"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="trading-calendar-card__select-info">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.3999 1.6001C5.7324 1.6001 5.9999 1.8676 5.9999 2.2001V3.2001H9.9999V2.2001C9.9999 1.8676 10.2674 1.6001 10.5999 1.6001C10.9324 1.6001 11.1999 1.8676 11.1999 2.2001V3.2001H11.9999C12.8824 3.2001 13.5999 3.9176 13.5999 4.8001V12.0001C13.5999 12.8826 12.8824 13.6001 11.9999 13.6001H3.9999C3.1174 13.6001 2.3999 12.8826 2.3999 12.0001V4.8001C2.3999 3.9176 3.1174 3.2001 3.9999 3.2001H4.7999V2.2001C4.7999 1.8676 5.0674 1.6001 5.3999 1.6001ZM5.3999 4.4001H3.9999C3.7799 4.5801 3.5999 4.5801 3.5999 4.8001V6.0001H12.3999V4.8001C12.3999 4.5801 12.2199 4.4001 11.9999 4.4001H5.3999ZM3.5999 7.2001V12.0001C3.5999 12.2201 3.7799 12.4001 3.9999 12.4001H11.9999C12.2199 12.4001 12.3999 12.2201 12.3999 12.0001V7.2001H3.5999Z"
                fill="white"
              />
            </svg>

            {dateRange && (
              <p className="trading-calendar-card__date">
                {dateRange.dateFrom || "-"}

                <span className="trading-calendar-card__separator">-</span>

                {dateRange.dateTo || "-"}
              </p>
            )}
          </div>

          <span>
            {isOpen ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.83348 9.9999C2.83348 10.1279 2.88213 10.2559 2.98013 10.3533C3.17546 10.5486 3.49215 10.5486 3.68748 10.3533L8.0008 6.03994L12.3141 10.3533C12.5094 10.5486 12.8261 10.5486 13.0215 10.3533C13.2168 10.1579 13.2168 9.84123 13.0215 9.6459L8.3548 4.97923C8.15947 4.7839 7.84278 4.7839 7.64744 4.97923L2.98078 9.6459C2.88211 9.7439 2.83348 9.8719 2.83348 9.9999Z"
                  fill="white"
                />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.83348 6.00004C2.83348 5.87204 2.88213 5.74402 2.98013 5.64668C3.17546 5.45135 3.49215 5.45135 3.68748 5.64668L8.0008 9.96L12.3141 5.64668C12.5094 5.45135 12.8261 5.45135 13.0215 5.64668C13.2168 5.84202 13.2168 6.15871 13.0215 6.35404L8.3548 11.0207C8.15947 11.216 7.84278 11.216 7.64744 11.0207L2.98078 6.35404C2.88211 6.25604 2.83348 6.12804 2.83348 6.00004Z"
                  fill="white"
                />
              </svg>
            )}
          </span>
        </button>

        {isOpen && isCustomCalendarOpen && (
          <div className="trading-calendar-card__custom-range-shell">
            <div className="trading-calendar-card__custom-range-shell-list">
              <div className="trading-calendar-card__dropdown">
                {filterOptions}
              </div>
            </div>

            <TradingCustomRangeCalendar
              key={`${customRange?.dateFrom ?? ""}-${
                customRange?.dateTo ?? ""
              }`}
              customRange={customRange}
              onApply={handleCustomRangeApply}
            />
          </div>
        )}

        {isOpen && !isCustomCalendarOpen && (
          <div className="trading-calendar-card__dropdown">
            {filterOptions}
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingCalendarCard;